import { z } from 'zod';

// Shared Zod validation primitives — one canonical definition per shape.

const DATE_SHAPE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_SHAPE = /^([01]\d|2[0-3]):[0-5]\d$/;

// Real calendar validity: parse [y,m,d] and round-trip through a local Date.
function isRealCalendarDate(value: string): boolean {
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
}

// YYYY-MM-DD with real calendar validity (rejects 2026-13-45, 2026-02-31, 2026-02-29 non-leap).
export const isoDate = z
  .string()
  .regex(DATE_SHAPE, 'Date must be a valid YYYY-MM-DD date')
  .refine(isRealCalendarDate, 'Date must be a valid YYYY-MM-DD date');

// HH:MM 24-hour clock (rejects 99:99).
export const isoTime = z.string().regex(TIME_SHAPE, 'Time must be HH:MM (24h)');

// ISO 8601 datetime for fields documented as datetime.
export const isoDateTime = z.string().datetime({ message: 'Must be an ISO datetime' });

// Pre-composed nullable/optional date helpers.
export const nullableDate = isoDate.nullable().optional();
export const optionalDate = isoDate.optional();

// Email primitives.
export const email = z.string().email().max(255);
export const optionalEmail = email.nullable().optional();
export const emailOrEmpty = z.string().trim().email().max(255).optional().or(z.literal(''));

// UUID primitive.
export const uuid = z.string().uuid();

// Shared shape for registering a browser-uploaded storage object. The browser uploads to
// Supabase directly (Vercel caps request bodies at ~4.5MB) and then reports the result here.
// Each route must still verify the path is inside its own school folder.
export const uploadedFileFields = {
  storagePath: z.string().min(1).max(500),
  fileName: z.string().min(1).max(255),
  fileSize: z.number().int().nonnegative().max(10 * 1024 * 1024),
  contentType: z.string().max(255),
};

// Cross-field ordering refinements. Each skips when either value is null/undefined.

// Reads a comparable string field off a parsed object; absent/null/non-string → undefined.
function readDateLike(value: Record<string, unknown>, key: string): string | undefined {
  const v = value[key];
  return typeof v === 'string' ? v : undefined;
}

// Asserts start <= end (lexical YYYY-MM-DD compares correctly), error on the end key.
export function withDateOrder<T extends z.ZodType<Record<string, unknown>>>(
  schema: T,
  startKey: string,
  endKey: string,
) {
  return schema.superRefine((value, ctx) => {
    const start = readDateLike(value, startKey);
    const end = readDateLike(value, endKey);
    if (start == null || end == null) return;
    if (start > end) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'End date must be on or after the start date', path: [endKey] });
    }
  });
}

// Asserts start < end (lexical HH:MM compares correctly), error on the end key.
export function withTimeOrder<T extends z.ZodType<Record<string, unknown>>>(
  schema: T,
  startKey: string,
  endKey: string,
) {
  return schema.superRefine((value, ctx) => {
    const start = readDateLike(value, startKey);
    const end = readDateLike(value, endKey);
    if (start == null || end == null) return;
    if (start >= end) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'End time must be after the start time', path: [endKey] });
    }
  });
}
